import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Star, FileText, Trophy, Users, Search, Shield, ArrowRight } from 'lucide-react';

const pillars = [
  { kanji: '義', name: 'Giustizia', description: 'Equità, Rispetto, Armonia' },
  { kanji: '勇', name: 'Coraggio', description: 'Innovazione, Audacia, Determinazione' },
  { kanji: '仁', name: 'Compassione', description: 'Empatia, Cura, Gentilezza' },
  { kanji: '礼', name: 'Rispetto', description: 'Cortesia, Tradizione, Dignità' },
  { kanji: '誠', name: 'Onestà', description: 'Trasparenza, Verità, Integrità' },
  { kanji: '忠義', name: 'Devozione', description: 'Dedizione, Passione, Fedeltà' },
  { kanji: '清', name: 'Purezza', description: 'Qualità, Semplicità, Autenticità' },
];

const features = [
  {
    icon: FileText,
    title: 'Modulo Recensione Etica',
    description: 'Sistema di valutazione basato sui 7 Pilastri con evidenze fotografiche',
    link: '/recensione',
    color: 'text-blue-400'
  },
  {
    icon: Trophy,
    title: 'Quiz Accreditamento',
    description: '12 domande per diventare Recensore Accreditato',
    link: '/quiz',
    color: 'text-yellow-400'
  },
  {
    icon: Users,
    title: 'Profili & Badge',
    description: 'Sistema di badge verificati e trasparenza attiva',
    link: '/profili',
    color: 'text-green-400'
  },
  {
    icon: Search,
    title: 'Ricerca & Discovery',
    description: 'Trova locali per pilastri etici e badge verificati',
    link: '/ricerca',
    color: 'text-purple-400'
  },
  {
    icon: Shield,
    title: 'Moderazione',
    description: 'Sistema di governance basato su Rei & Makoto',
    link: '/moderazione',
    color: 'text-red-400'
  }
];

export default function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-bushido relative py-24 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="space-y-4">
              <h1 className="font-bushido text-5xl md:text-7xl font-bold">
                <span className="gold-accent">BushiFood</span>
              </h1>
              <p className="font-kanji text-xl md:text-2xl text-muted-foreground">
                The Chef Samurai
              </p>
              <div className="flex justify-center space-x-4 text-2xl font-kanji">
                <span className="text-secondary">武士道</span>
                <span className="text-accent">×</span>
                <span className="text-secondary">料理</span>
              </div>
            </div>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Sistema etico di recensione ristoranti basato sui <strong className="text-secondary">Sette Pilastri del Bushidō</strong>. 
              Dove ogni ingrediente è un cittadino del piatto e ogni chef un guardiano dell'onore culinario.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/recensione">
                <Button size="lg" className="btn-samurai px-8 py-3">
                  <FileText className="mr-2 h-5 w-5" />
                  Scrivi Recensione
                </Button>
              </Link>
              <Link to="/pilastri">
                <Button size="lg" variant="outline" className="btn-honor px-8 py-3">
                  <Star className="mr-2 h-5 w-5" />
                  Scopri i Pilastri
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* I Sette Pilastri */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-bushido text-4xl font-bold mb-4">
              I Sette Pilastri del <span className="gold-accent">Bushidō</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Ogni recensione è guidata dai principi etici del codice samurai, 
              applicati all'arte culinaria moderna.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {pillars.map((pillar, index) => (
              <Card key={index} className="pillar-card group cursor-pointer">
                <CardHeader className="text-center pb-4">
                  <div className="kanji-display mx-auto mb-2 group-hover:scale-110 transition-transform duration-300">
                    {pillar.kanji}
                  </div>
                  <CardTitle className="font-bushido text-xl">
                    {pillar.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription className="text-sm">
                    {pillar.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/pilastri">
              <Button variant="outline" className="btn-honor">
                Esplora tutti i Pilastri
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Funzionalità Principali */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-bushido text-4xl font-bold mb-4">
              Ecosistema <span className="gold-accent">Etico</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Un sistema completo per recensioni etiche, accreditamento recensori 
              e discovery di locali basata sui valori.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Link key={index} to={feature.link}>
                  <Card className="pillar-card h-full group">
                    <CardHeader>
                      <div className={`${feature.color} mb-4`}>
                        <Icon className="h-12 w-12 group-hover:scale-110 transition-transform duration-300" />
                      </div>
                      <CardTitle className="font-bushido text-xl">
                        {feature.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed">
                        {feature.description}
                      </CardDescription>
                      <div className="mt-4">
                        <Button variant="ghost" className="p-0 h-auto text-secondary hover:text-accent">
                          Scopri di più
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4 bg-gradient-to-r from-background via-muted/50 to-background">
        <div className="container mx-auto text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-bushido text-4xl font-bold">
              Diventa un <span className="gold-accent">Recensore Accreditato</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Completa il quiz di accreditamento e ottieni il badge "Recensore Accreditato". 
              12 domande per dimostrare la tua comprensione dei principi etici del BushiFood.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quiz">
                <Button size="lg" className="btn-samurai px-8 py-3">
                  <Trophy className="mr-2 h-5 w-5" />
                  Inizia Quiz
                </Button>
              </Link>
              <Badge variant="secondary" className="px-4 py-2 text-sm">
                <span className="font-kanji mr-2">認定</span>
                Accreditamento Ufficiale
              </Badge>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}